import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Checkbox } from "@/shared/ui/checkbox";
import { TagColorPalette } from "./TagColorPalette";
import type { Tag, UpdateTagRequest } from "@/services/api/tags";
import { useState, useEffect, useId } from "react";

interface TagEditDialogProps {
  open: boolean;
  tag: Tag | null;
  isLoading?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (id: string | number, data: UpdateTagRequest) => void;
}

export function TagEditDialog({
  open,
  tag,
  isLoading,
  onOpenChange,
  onSubmit,
}: TagEditDialogProps) {
  const descriptionId = useId();
  const [name, setName] = useState("");
  const [color, setColor] = useState("#E5E7EB");
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (tag) {
      setName(tag.name ?? "");
      setColor(tag.color || "#E5E7EB");
      setIsActive(tag.isActive);
    }
  }, [tag]);

  if (!tag) return null;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    onSubmit(tag.id, {
      name: name || null,
      color,
      isActive,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={descriptionId}>
        <DialogHeader>
          <DialogTitle>Edit tag</DialogTitle>
          <DialogDescription>
            Update the tag information below. You can modify the name, color,
            and active status.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-ink-800">Tag name</label>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Enter tag name"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-ink-800 mr-4">
              Color
            </label>
            <TagColorPalette value={color} onChange={setColor} />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="tag-active"
              checked={isActive}
              onCheckedChange={(checked) => setIsActive(checked === true)}
            />
            <label
              htmlFor="tag-active"
              className="text-sm font-medium text-ink-800 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Active
            </label>
          </div>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
